import { useCallback } from 'react';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { useTracking } from 'react-tracking';
import { Button, cn } from '@simplywallst/ui-core/next';
import { Dropdown } from '@/components/Dropdown';
import SortingOutline from '@simplywallst/ui-core/icons/SortingOutline';
import type { ButtonRenderProps } from '@/components/Dropdown';

type SortingOption = 'popular' | 'undervalued' | 'overvalued';
type Props = {
  sortHash: SortingOption | null;
  changeSort: (newSort: SortingOption | null) => void;
};
export const NarrativeCommunitySort = ({ sortHash, changeSort }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const { trackEvent } = useTracking();

  const sortingOptions: { option: SortingOption | 'none'; label: string }[] = [
    {
      option: 'undervalued',
      label: 'Undervalued',
    },
    {
      option: 'overvalued',
      label: 'Overvalued',
    },
    {
      option: 'none',
      label: 'None',
    },
  ];

  sortingOptions.unshift({
    option: 'popular',
    label: 'Popular',
  });

  const sortingButton = useCallback(
    ({ onBlur, onFocus, onClick, innerRef }: ButtonRenderProps) => {
      const sortingLabel =
        sortingOptions.find((o) => o.option === sortHash)?.label || 'Sort';
      return (
        <Button
          variant={`${sortHash ? 'secondary' : 'outline'}`}
          size="medium"
          type="button"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
        >
          <SortingOutline aria-label="Community Sort" /> {sortingLabel}
        </Button>
      );
    },
    [sortHash]
  );

  if (isMobileScreen) {
    return (
      <Dropdown
        name="sorting-selection"
        align="right"
        displayType="inline-block"
        options={sortingOptions}
        buttonRender={sortingButton}
        value={sortHash || ''}
        onSelect={(value) => {
          const targetSort = value === 'none' ? null : value;
          changeSort(targetSort as SortingOption);
        }}
      />
    );
  }

  return (
    <div className={cn('grid gap-x0_5', 'grid-cols-[repeat(3,max-content)]')}>
      <Button
        type="button"
        size="medium"
        variant={sortHash === 'popular' ? 'secondary' : 'outline'}
        onClick={() => {
          const targetSort = sortHash === 'popular' ? null : 'popular';
          changeSort(targetSort);
          trackEvent({
            action: 'click',
            subject: 'sort',
            modifier: targetSort,
          });
        }}
      >
        Popular
      </Button>
      <Button
        type="button"
        size="medium"
        variant={sortHash === 'undervalued' ? 'secondary' : 'outline'}
        onClick={() => {
          const targetSort = sortHash === 'undervalued' ? null : 'undervalued';
          changeSort(targetSort);
          trackEvent({
            action: 'click',
            subject: 'sort',
            modifier: targetSort,
          });
        }}
      >
        Undervalued
      </Button>
      <Button
        type="button"
        size="medium"
        variant={sortHash === 'overvalued' ? 'secondary' : 'outline'}
        onClick={() => {
          const targetSort = sortHash === 'overvalued' ? null : 'overvalued';
          changeSort(targetSort);
          trackEvent({
            action: 'click',
            subject: 'sort',
            modifier: targetSort,
          });
        }}
      >
        Overvalued
      </Button>
    </div>
  );
};
